/**
 * Hat customiser (#388).
 *
 * Adds the hat and every chosen accessory as separate cart lines that share a `_bundleId` line item
 * property. The cart transform function in akubra-extensions groups those lines into one bundle at
 * checkout. Prices are never sent from here, only variant IDs, so the buyer pays whatever Shopify has
 * on each variant.
 *
 * `<product-form>` posts FormData and can only express a single item, which is why this component
 * owns its own add to cart rather than hooking the standard one.
 */
/*
 * One 2d context reused for every engraving measurement, created lazily on first use. Text metrics need no
 * pixels, so this canvas is never attached to the document and never painted; a fresh one per keystroke
 * would allocate a new backing store on every character typed.
 */
let engravingMetrics = null;

document.addEventListener('alpine:init', function () {
  Alpine.store('customiser', {
    // ---------------------------------------------------------------------------------------------
    // Hat and flags
    // ---------------------------------------------------------------------------------------------
    enabled: false,
    mode: 'product',
    formId: '',
    hatProductId: null,
    hatVariantId: null,
    hatPrice: 0,
    hatAvailable: true,
    bases: { exterior: null, interior: null, accessory: null },

    // Catalogue, snapshotted from the per-instance config element rather than a global.
    accessories: [],
    itemsById: {},
    engraving: null,
    strings: {},

    // ponytail: one unit per accessory until the cart transform stops merging on
    // Math.min(...quantities) (akubra-extensions run.ts:122). Today a hat at 1 and a pin at 2 merges
    // one pin and strands the second as a loose line still carrying _bundleId, and the merged line
    // reports quantity 1 while charging for 2. Raise this from config once that ships.
    maxUnitQty: 1,

    /* How far each extra copy of one accessory shifts across, per copy, as a percentage of the preview.
       Merchant-set (see `unit_stagger`). 4 rather than the original hardcoded 3, which overlapped so far
       that copies read as one smudged pin, and rather than the 8 that replaced it, which cleared a whole
       pin head and read as two separate pins side by side. 4 leaves them about half overlapped, which is a
       fan. */
    unitStagger: 2,

    /**
     * The same spread on the vertical axis, 0 by default so nothing moves until a merchant asks for it.
     * Pins on a brim read better fanned sideways; a crown may want them dropping down the side instead,
     * and a diagonal wants both. Hence two values rather than one plus an axis switch: 0 on an axis
     * disables it, so the pair expresses across, down and diagonal without a third setting.
     */
    unitStaggerY: 0,

    /**
     * How many chips the tray held last time we looked, so a GROWING tray can be told from a re-render.
     * -1 rather than 0, so the first pass counts as growth and a restored link carrying a full bundle
     * opens at the newest chip rather than the oldest.
     */
    trayUnitCount: -1,

    /** The tray scroller, seeded by observeTray on each adopt(). */
    trayEl: null,

    /* The accessory whose ADD button is currently showing ADDED, and the timer that clears it. Held as a
       variant id rather than a boolean so a confirmation on one accessory cannot bleed onto another. */
    justAddedId: null,
    justAddedTimer: null,

    // ---------------------------------------------------------------------------------------------
    // Uncommitted UI state
    // ---------------------------------------------------------------------------------------------
    activeHandle: null,
    highlightedId: null,
    // #388 C7: the cart line this session is replacing, from EDIT's `edit` param. Null on a normal
    // visit. Retired only after the replacement is safely in the cart.
    editingLineKey: null,

    /* Hats on the line being edited; 1 for a fresh build, which is what the PDP always adds. */
    editingUnits: 1,
    // `adopt` re-runs after any section replacement, so the URL must be read exactly once.
    hydratedFromUrl: false,
    // Ditto for the effect that writes it back.
    urlSyncBound: false,

    // Mobile summary sheet, opened from the sticky bar's chevron.
    summaryOpen: false,
    detailsOpen: false,
    draftQty: 1,
    engravingDraft: '',
    // Set while the engraving field holds focus, so the preview can show the underside the laser etches.
    // DOM focus, unlike focusedItem(), which is the highlighted swatch. Needs to be store state rather
    // than a document.activeElement read because baseImage() is an Alpine getter: activeElement changing
    // fires no reactivity, so the preview would keep the stale base until something else re-rendered it.
    engravingFocused: false,

    // Committed selections, in add order.
    entries: [],

    adding: false,
    error: '',

    // Read by the one polite live region in the composition root. Set by every mutating method, so a
    // screen reader hears add, remove and clear, each with the running count for orientation.
    announcement: '',

    // ---------------------------------------------------------------------------------------------
    // Lifecycle
    // ---------------------------------------------------------------------------------------------

    /**
     * Alpine calls this once when the store is registered, so the listener is attached exactly once.
     * adopt() re-runs on every DOM replacement and would stack handlers if it owned this.
     */
    init: function () {
      const self = this;
      document.addEventListener('variant:change', function (event) {
        self.onVariantChange(event);
      });
    },

    /**
     * Reads the per-instance config element. Called from x-init on the panel, so it re-seeds itself
     * after a full section replacement instead of going stale the way a global would.
     */
    adopt: function (rootEl) {
      const script = rootEl.querySelector('[data-customiser-config]');
      if (!script) return;

      let config;
      try {
        config = JSON.parse(script.textContent);
      } catch (parseError) {
        this.error = 'The customiser could not start.';
        return;
      }

      // A different product means a fresh page; the same one means a re-render, so keep selections.
      const isSameProduct = this.hatProductId === config.hatProductId;

      this.formId = config.formId || '';
      this.hatProductId = config.hatProductId || null;
      this.hatVariantId = config.hatVariantId || null;
      this.hatPrice = config.hatPrice || 0;
      this.hatAvailable = config.hatAvailable !== false;
      this.bases = config.bases || { exterior: null, interior: null, accessory: null };
      this.accessories = config.accessories || [];
      this.engraving = config.engraving || null;
      this.strings = config.strings || {};
      if (config.maxUnitQty) this.maxUnitQty = config.maxUnitQty;
      // Number(), not a truthiness check: 0 is a legitimate value meaning "stack them exactly".
      if (config.unitStagger !== undefined && config.unitStagger !== null) {
        this.unitStagger = Number(config.unitStagger);
      }
      if (config.unitStaggerY !== undefined && config.unitStaggerY !== null) {
        this.unitStaggerY = Number(config.unitStaggerY);
      }

      this.itemsById = {};
      const self = this;
      this.accessories.forEach(function (category) {
        (category.items || []).forEach(function (item) {
          self.itemsById[String(item.id)] = item;
        });
      });
      if (this.engraving) this.itemsById[String(this.engraving.variantId)] = this.engraving;

      if (!isSameProduct) {
        this.entries = [];
        this.engravingDraft = '';
        this.highlightedId = null;
        this.detailsOpen = false;
        this.draftQty = 1;
        this.mode = 'product';
      }

      if (!this.activeHandle && this.accessories.length) {
        this.activeHandle = this.accessories[0].handle;
      }

      // After the reset above, never before: on a first visit that branch clears `entries`, so
      // hydrating earlier would have its work thrown away.
      this.hydrateFromUrl();

      // Registered once, and only after hydrateFromUrl: Alpine.effect runs its callback immediately, so
      // registering earlier would write an empty `customise` over the incoming one before it was read.
      // adopt() re-runs on every section replacement, hence the guard.
      if (!this.urlSyncBound) {
        this.urlSyncBound = true;
        Alpine.effect(function () {
          self.syncUrl();
        });
      }

      // Theme editor only. Every settings change reloads the preview, and the store comes back with
      // mode 'product', so the panel closes and the accessories vanish: a merchant tuning the placement
      // sliders had to reopen the customiser and re-add a pin after EVERY nudge, just to see what the
      // nudge did. Reported 1 September while setting up the Arena template.
      //
      // sessionStorage rather than the URL, because syncUrl() deliberately writes nothing while mode is
      // 'product', and the editor reloads to its own address anyway, so the query string is not ours to
      // rely on here.
      //
      // Guarded on designMode, so a shopper's session neither reads nor writes any of this.
      if (this.inDesignMode()) {
        this.restoreDesignState();

        if (!this.designStateBound) {
          this.designStateBound = true;
          Alpine.effect(function () {
            self.saveDesignState();
          });
        }
      }

      this.enabled = true;
      this.applyMode();
      this.observeTabs(rootEl);
      this.observeTray(rootEl);
      this.revealLatestUnit();
    },

    /** True only inside the theme editor. */
    inDesignMode: function () {
      return Boolean(window.Shopify && window.Shopify.designMode);
    },

    /**
     * The key carries the product id, so switching products in the editor does not restore another
     * hat's pins onto this one.
     */
    designStateKey: function () {
      return 'hat-customiser:design:' + this.hatProductId;
    },

    saveDesignState: function () {
      // Read every tracked value BEFORE the try, so Alpine.effect registers its dependencies even on a
      // session where storage throws. Otherwise the effect would never re-run and the state would freeze
      // at whatever it was first time through.
      const state = {
        mode: this.mode,
        entries: this.entries.map(function (entry) {
          return { variantId: entry.variantId, handle: entry.handle, quantity: entry.quantity, properties: entry.properties || {} };
        }),
        engravingDraft: this.engravingDraft,
        activeHandle: this.activeHandle,
        highlightedId: this.highlightedId,
        detailsOpen: this.detailsOpen,
      };

      try {
        sessionStorage.setItem(this.designStateKey(), JSON.stringify(state));
      } catch (storageError) {
        // Private windows and quota limits both land here. The editor simply behaves as it did before.
      }
    },

    restoreDesignState: function () {
      let state;
      try {
        const raw = sessionStorage.getItem(this.designStateKey());
        if (!raw) return;
        state = JSON.parse(raw);
      } catch (storageError) {
        return;
      }
      if (!state) return;

      // An accessory the merchant has just removed from the collection must not come back from storage,
      // so every id is re-checked against the freshly parsed config, exactly as hydrateFromUrl does.
      const self = this;
      const entries = (state.entries || []).filter(function (entry) {
        return Boolean(self.itemsById[String(entry.variantId)]);
      });

      // Never overwrite a URL that already carries a build: hydrateFromUrl has run by this point, and an
      // EDIT link the merchant just opened is more current than the last thing they had on screen.
      if (!this.entries.length) {
        this.entries = entries;
      }

      if (state.engravingDraft && !this.engravingDraft) this.engravingDraft = state.engravingDraft;
      if (state.activeHandle && this.itemsByHandleExists(state.activeHandle)) this.activeHandle = state.activeHandle;
      if (state.highlightedId && this.itemsById[String(state.highlightedId)]) {
        this.highlightedId = state.highlightedId;
        this.detailsOpen = Boolean(state.detailsOpen);
      }

      // Last, because applyMode() runs straight after adopt() and reads it.
      if (state.mode === 'customise') this.mode = 'customise';
    },

    /** Whether a stored category handle still exists in the current config. */
    itemsByHandleExists: function (handle) {
      return this.accessories.some(function (category) {
        return category.handle === handle;
      });
    },

    /**
     * #388 C7: rebuilds a bundle from the cart's EDIT link.
     *
     * Every id is checked against `itemsById` rather than trusted, because the URL is user-editable and
     * a stale link survives the merchant unpublishing an accessory. An unknown or unavailable id is
     * dropped quietly: the buyer came here to edit a hat, and a validation error about a pin they can no
     * longer buy would be noise.
     *
     * Only `edit` is stripped afterwards, so that a refresh or a shared address bar cannot re-enter edit
     * mode and retire a cart line a second time. `customise` and `engrave` are LEFT IN PLACE: syncUrl()
     * now owns them, and deleting them here would just fight it and lose the bundle on the next refresh.
     */
    hydrateFromUrl: function () {
      if (this.hydratedFromUrl) return;
      this.hydratedFromUrl = true;

      let params;
      try {
        params = new URL(window.location.href).searchParams;
      } catch (urlError) {
        return;
      }

      const spec = params.get('customise');
      if (!spec) return;

      const engravingVariantId = this.engraving ? String(this.engraving.variantId) : null;
      const self = this;
      let restored = 0;

      spec.split(',').forEach(function (pair) {
        const parts = pair.split(':');
        const variantId = String(parts[0] || '').trim();
        if (!variantId) return;

        // Clamped to the same ceiling the stepper uses, so a hand-edited URL cannot exceed it.
        const quantity = Math.min(self.maxUnitQty, Math.max(1, parseInt(parts[1], 10) || 1));

        if (engravingVariantId && variantId === engravingVariantId) {
          const text = params.get('engrave');
          if (text) self.engravingDraft = text.slice(0, (self.engraving && self.engraving.max) || 10);
          restored += 1;
          return;
        }

        const item = self.itemsById[variantId];
        if (!item || item.available === false) return;

        self.entries.push({
          variantId: item.id,
          handle: item.handle,
          quantity: quantity,
          properties: {},
        });
        restored += 1;
      });

      if (!restored) return;

      this.editingLineKey = params.get('edit') || null;

      /*
       * How many hats the line being edited holds, so the replacement restores the SAME number.
       *
       * Without it, EDIT retired the line and added one hat back, so a 3-hat line became 1 + 2: two lines
       * for the same hat, one with the edited add-ons and one with the originals. The design draws one line
       * per configuration with a quantity and a single EDIT, so the line is edited as a unit.
       */
      this.editingUnits = Math.max(1, parseInt(params.get('units'), 10) || 1);
      this.mode = 'customise';

      const url = new URL(window.location.href);
      url.searchParams.delete('edit');
      window.history.replaceState({}, '', url.toString());
    },

    /**
     * Mirrors the committed bundle into the address bar in exactly the format hydrateFromUrl() parses,
     * so a refresh rebuilds the same tray.
     *
     * Driven by one Alpine.effect (registered in adopt) rather than called from commit, removeUnit,
     * clear and the engraving field: the effect tracks `entries`, `engravingDraft` and `mode` itself, so
     * a sixth mutator cannot forget to call it. That is also why this reads state and writes, and takes
     * no arguments.
     *
     * `edit` is NEVER written back. It means "retire this cart line once the replacement is added", so
     * persisting it would let a refresh, or a shared link, silently delete a cart line on the next add
     * to cart.
     *
     * replaceState, never pushState: every swatch tap would otherwise become a history entry and the
     * back button would crawl backwards through the build instead of leaving the page.
     */
    syncUrl: function () {
      // Only in customise mode. Params on the plain product view would describe a build the buyer
      // cannot see. Leaves whatever is already there alone rather than deleting, so stepping out to the
      // product view and back does not throw the bundle away.
      if (this.mode !== 'customise') return;

      let url;
      try {
        url = new URL(window.location.href);
      } catch (urlError) {
        return;
      }

      const pairs = this.entries.map(function (entry) {
        return entry.variantId + ':' + entry.quantity;
      });

      // The engraving rides in `customise` as its own variant id, because that is how hydrateFromUrl
      // recognises it, with the text itself in `engrave`.
      const text = this.engravingText();
      if (text && this.engraving) pairs.push(this.engraving.variantId + ':1');

      // Deleted rather than left empty, so a cleared customiser gives a clean URL.
      if (pairs.length) url.searchParams.set('customise', pairs.join(','));
      else url.searchParams.delete('customise');

      if (text) url.searchParams.set('engrave', text);
      else url.searchParams.delete('engrave');

      window.history.replaceState({}, '', url.toString());
    },

    /**
     * `<x-tabs>` never dispatches an event on tab change: click, arrow keys and the theme editor's
     * `shopify:block:select` all funnel through setting `selectedIndex`, which writes the
     * `selected-index` attribute (`assets/theme.js` observedAttributes). Observing that attribute is
     * therefore the only hook that catches all three, where an `x-on:click` on the tab buttons would
     * catch the mouse alone and leave `activeHandle` stale for keyboard users, silently breaking
     * `baseImage()`.
     */
    /**
     * Keeps the tray pinned to its right-hand end unless the buyer has scrolled it away from there.
     *
     * The tray is a horizontal scroller. It sat at scrollLeft 0 forever, so past about six accessories
     * pressing ADD did nothing visible: the new chip and the [+] slot were both off the right edge.
     *
     * The pin is driven by the chip COUNT, not by watching where the buyer has scrolled to. See
     * revealLatestUnit for why the scroll-position version could not be made to work.
     */
    observeTray: function (rootEl) {
      const tray = rootEl.querySelector('.hat-customiser__tray');
      if (!tray) return;

      // Re-seeded on every adopt(), so a section replacement in the theme editor cannot leave
      // revealLatestUnit holding a detached node.
      this.trayEl = tray;
      if (tray.dataset.customiserObserved === 'true') return;
      tray.dataset.customiserObserved = 'true';

      const self = this;

      // childList and subtree: a chip is an <li> the tray gains, but Alpine also swaps content inside an
      // existing chip, and both change the scroll width.
      const pin = function () {
        requestAnimationFrame(function () {
          self.revealLatestUnit();
        });
      };
      new MutationObserver(pin).observe(tray, { childList: true, subtree: true });
      pin();
    },

    observeTabs: function (rootEl) {
      const tabs = rootEl.querySelector('x-tabs');
      if (!tabs || tabs.dataset.customiserObserved === 'true') return;
      tabs.dataset.customiserObserved = 'true';

      const self = this;
      const sync = function () {
        const index = parseInt(tabs.getAttribute('selected-index') || '0', 10);
        const category = self.accessories[index];
        if (category && category.handle !== self.activeHandle) self.selectCategory(category.handle);
      };

      new MutationObserver(sync).observe(tabs, {
        attributes: true,
        attributeFilter: ['selected-index'],
      });
      sync();
    },

    /**
     * The theme announces variant changes on the product form. `form.id` returns the <input name="id">
     * element via the named getter rather than the form's id string, and event.detail.formId is that
     * same element, so the attribute is the only reliable comparison.
     */
    onVariantChange: function (event) {
      const target = event.target;
      if (!target || typeof target.getAttribute !== 'function') return;
      if (this.formId && target.getAttribute('id') !== this.formId) return;

      const variant = event.detail && event.detail.variant;
      if (!variant) return;

      this.hatPrice = variant.price;
      this.hatVariantId = variant.id;
      this.hatAvailable = variant.available !== false;
    },

    /**
     * Written to <html>, not to `.product`.
     *
     * `.product` was wrong twice over. It is not an ancestor of everything that needs to react: the
     * sticky bar sits outside it deliberately, so that theme.js's non-partial `<product-rerender>`
     * wrapper cannot destroy it on a size change, and a `.product`-scoped attribute left the bar
     * permanently hidden. Verified on staging: the bar measured `display: none` in customise mode.
     * It also assumed `.product` exists, which couples the mode to one theme's markup.
     *
     * The root element is an ancestor of every surface involved (buy-box panel, gallery, sticky bar) and
     * always present. Still resolved at call time rather than cached, because theme.js replaceWith()es
     * subtrees and a held reference goes detached.
     */
    applyMode: function () {
      document.documentElement.setAttribute('data-customiser-mode', this.mode);
    },

    setMode: function (mode) {
      // Keyboard users land on the control that just appeared rather than at the top of the document.
      //
      // Order matters, and it is not obvious. The control that triggered this call is usually inside
      // the subtree about to be hidden (Back to product lives in the panel). If it still holds focus
      // when the subtree goes `display: none`, the browser blurs it to <body> during the following
      // rendering update, which lands AFTER our focus() and silently clobbers it. So drop focus
      // first, then flip the mode, then claim it. Reading offsetHeight flushes style so the new
      // target is rendered and therefore focusable: focus() on an unrendered element is a no-op.
      //
      // Verified on staging 19 Aug. requestAnimationFrame alone does not fix this, and neither does
      // the reflow alone; the blur-off is the part that matters.
      if (document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }

      this.mode = mode;
      this.applyMode();

      const target = document.getElementById(
        mode === 'customise' ? 'hat-customiser-panel' : 'hat-customiser-trigger'
      );
      if (!target) return;

      void target.offsetHeight;
      target.focus();
    },

    // ---------------------------------------------------------------------------------------------
    // Selection
    // ---------------------------------------------------------------------------------------------
    selectCategory: function (handle) {
      this.activeHandle = handle;
      this.highlightedId = null;
      this.detailsOpen = false;
      this.draftQty = 1;
    },

    highlight: function (variantId) {
      this.highlightedId = String(variantId);

      // Open on selection, per the annotation on Figma 445:33630 ("Description to opened by default").
      // commit() closes it again, so the description shows while the buyer is deciding and gets out of
      // the way once the accessory is in the tray.
      this.detailsOpen = true;

      // 1, not the committed quantity: the stepper means "how many more", see commit().
      this.draftQty = 1;
    },

    toggleDetails: function () {
      this.detailsOpen = !this.detailsOpen;
    },

    /**
     * Scrolls the add-on tray by one slot.
     *
     * The element is passed in rather than looked up, because the store holds no DOM references and a
     * querySelector here would find the wrong tray if two customisers ever shared a page.
     *
     * Steps by one slot plus its gap, read off the live slot rather than hardcoded, so it stays correct
     * at every breakpoint where the tray's flex arithmetic gives a different slot width.
     */
    scrollTray: function (trayEl, direction) {
      if (!trayEl) return;
      const slot = trayEl.querySelector('.hat-customiser__slot');
      const step = slot ? slot.offsetWidth + 12 : 90;
      trayEl.scrollBy({ left: direction * step, behavior: 'smooth' });
    },

    /**
     * Past six slots the tray scrolls, which is the threshold the design annotation states. Counted from
     * the model rather than measured off scrollWidth: the annotation is about slot COUNT, and a measured
     * overflow check would also fire at narrow widths where six slots already do not fit, showing arrows
     * the design does not ask for.
     */
    trayScrolls: function () {
      return this.units().length + 1 > 6;
    },

    toggleSummary: function () {
      this.summaryOpen = !this.summaryOpen;
    },

    closeSummary: function () {
      this.summaryOpen = false;
    },

    stepQty: function (delta) {
      const next = this.draftQty + delta;
      this.draftQty = Math.min(this.maxUnitQty, Math.max(1, next));
    },

    /**
     * Upsert. The draft quantity is ADDED to what is already committed, not assigned over it.
     *
     * It used to assign, on the reasoning that a double press should be idempotent. The cost of that was
     * larger than the accident it prevented: the stepper is seeded from the committed quantity, so a
     * buyer who had added one pin and wanted a second found the stepper reading 1, the button reading
     * ADD, and the press doing nothing at all while still flashing ADDED at them. Reported 1 September.
     * The only route to two of the same accessory was to notice the stepper and raise it first.
     *
     * `highlight()` now seeds the stepper to 1 rather than to the committed quantity, which is the other
     * half of this change: the stepper answers "how many more", so pressing ADD twice on a stepper
     * showing 2 gives four, and that is the arithmetic the buyer just asked for both times.
     */
    commit: function () {
      if (!this.highlightedId) return;

      const item = this.itemsById[this.highlightedId];
      if (!item || item.available === false) return;

      const existing = this.entries.find(function (entry) {
        return entry.variantId === item.id;
      });

      if (existing) {
        const next = Math.min(this.maxUnitQty, existing.quantity + this.draftQty);

        // At the ceiling the press genuinely does nothing, so it must not flash ADDED: confirming an
        // action that did not happen is the defect this whole change exists to remove.
        if (next === existing.quantity) {
          this.announce(
            (this.strings.maxAnnounce || 'You have the maximum of') + ' ' + this.maxUnitQty + ' ' + item.title + '.'
          );
          return;
        }

        existing.quantity = next;
      } else {
        this.entries.push({
          variantId: item.id,
          handle: item.handle,
          quantity: Math.min(this.maxUnitQty, this.draftQty),
          properties: {},
        });
      }

      // Deliberately does NOT close the details panel. highlight() opens it on selection per the
      // Figma 445:33630 annotation, and the description stays up after ADD so the buyer can keep
      // reading what they just added. selectCategory() still closes it: a tab change is a new context
      // with no selected accessory to describe.
      this.flashAdded(item.id);
      this.announce((this.strings.addedAnnounce || 'Added') + ' ' + item.title + '.');
    },

    /**
     * Parks the tray at its right-hand end, so the newest chip and the [+] slot are both in view.
     *
     * The tray is a horizontal scroller with its own arrows, and it stayed at scrollLeft 0 no matter how
     * many accessories were chosen, so past about six the buyer pressed ADD and nothing visibly happened:
     * the new chip and the [+] slot were both off the right edge. Reported 1 September.
     *
     * The end, rather than the new chip's own offset. The newest unit is always last and the [+] slot
     * follows it, and the request was to see both, so scrolling to the end satisfies it without measuring
     * a chip that Alpine has not rendered yet.
     *
     * Fires only when the chip count GROWS, so the tray moves when the buyer adds something and at no
     * other time. Their own scrolling between adds changes no chips, so it is left alone.
     *
     * This replaced an "is the tray parked at its end" flag recomputed from scroll events, which poisoned
     * itself: setting scrollLeft dispatches a scroll event asynchronously, and by the time the handler
     * ran, layout already included the new chip, so it compared the OLD maximum against the NEW one,
     * concluded "not at the end", and blocked every later pin. Eight adds sat at 84 of 417 with the flag
     * stuck false. A count cannot race: nothing but an add changes it.
     *
     * Driven by a MutationObserver on the tray, NOT from commit(). Two attempts at calling it from
     * commit() failed the same way: the scroll ran before Alpine had laid the new chip out, so scrollWidth
     * was still the old value and the browser clamped the jump to the OLD maximum. Eight quick adds landed
     * at 84 of 417, and a single add moved 501 to 504 of 585, which looks exactly like the bug being
     * fixed. Waiting two animation frames was not enough either. Reacting to the mutation runs it once the
     * chip exists.
     *
     * A tray that does not overflow is left alone: scrolling a non-scrolling box is a no-op in most
     * browsers but still invalidates layout.
     */
    revealLatestUnit: function () {
      const tray = this.trayEl;
      if (!tray) return;

      const count = tray.querySelectorAll('.hat-customiser__slot:not(.hat-customiser__slot--empty)').length;
      const grew = count > this.trayUnitCount;
      this.trayUnitCount = count;
      if (!grew) return;

      if (tray.scrollWidth <= tray.clientWidth) return;

      /*
       * scrollLeft rather than scrollTo, and instant rather than smooth.
       *
       * The browser clamps an over-large scrollLeft to the maximum, which is exactly the behaviour wanted.
       *
       * Instant, because a smooth scroll animates across frames while further adds land, and the tray
       * visibly chases itself. An instant jump of about one chip on a strip this size is barely
       * perceptible, and it is what the request asks for literally. Nothing animates, so there is no
       * motion to gate on prefers-reduced-motion.
       */
      tray.scrollLeft = tray.scrollWidth;
    },

    qtyOf: function (variantId) {
      const entry = this.entries.find(function (candidate) {
        return String(candidate.variantId) === String(variantId);
      });
      return entry ? entry.quantity : 0;
    },

    /**
     * ADDED is a CONFIRMATION of the press just made, so it fades after three seconds and the button
     * returns to ADD.
     *
     * It was additionally gated on `committed === draftQty`, which encoded the old assigning commit():
     * the label meant "the tray holds exactly what the stepper shows". Now that a press adds rather than
     * assigns, that comparison has no meaning, and keeping it would blank the confirmation on precisely
     * the press this change exists to enable, the second ADD on an accessory already in the tray.
     *
     * The button returning to ADD is now honest in both directions: it reads as live, and it is.
     */
    isAdded: function (variantId) {
      return String(this.justAddedId) === String(variantId);
    },

    /** Shows ADDED on one accessory and takes it away again. */
    flashAdded: function (variantId) {
      if (this.justAddedTimer) clearTimeout(this.justAddedTimer);
      this.justAddedId = variantId;

      const self = this;
      this.justAddedTimer = setTimeout(function () {
        self.justAddedId = null;
        self.justAddedTimer = null;
      }, 3000);
    },

    /** One X on one chip removes one unit, because the design renders quantity 2 as two chips. */
    removeUnit: function (variantId) {
      /*
       * The engraving is not an entry. `chosen()` SYNTHESISES it from `engravingDraft`, so it has a chip in
       * the tray but no row in `entries`, and the lookup below found nothing and returned silently: the X
       * on the engraving chip did nothing at all. Reported 24 Aug.
       *
       * Removing it means clearing the text it is derived from. `engravingOn()` then goes false and the chip
       * disappears on its own, which is the same thing the panel's own REMOVE button does.
       *
       * Handled here rather than by giving the engraving a real entry, because `entries` is what gets posted
       * to the cart as accessories and the engraving needs its typed text carried as a property instead.
       * One entry point, so any future chip control gets this for free.
       */
      if (this.engraving && String(this.engraving.variantId) === String(variantId)) {
        this.engravingDraft = '';
        this.announce(
          (this.strings.removedAnnounce || 'Removed') + ' ' + (this.engraving.title || 'engraving') + '.'
        );
        return;
      }

      const index = this.entries.findIndex(function (entry) {
        return String(entry.variantId) === String(variantId);
      });
      if (index === -1) return;

      const entry = this.entries[index];
      entry.quantity -= 1;
      if (entry.quantity < 1) this.entries.splice(index, 1);

      if (String(this.highlightedId) === String(variantId)) {
        this.draftQty = 1;
      }

      const removed = this.itemsById[String(variantId)];
      this.announce((this.strings.removedAnnounce || 'Removed') + ' ' + (removed ? removed.title : '') + '.');
    },

    clear: function () {
      this.entries = [];
      this.engravingDraft = '';
      this.draftQty = 1;
      this.detailsOpen = false;
      this.summaryOpen = false;
      this.announce(this.strings.clearedAnnounce || 'Add-ons cleared.');
    },

    // ---------------------------------------------------------------------------------------------
    // Derived
    // ---------------------------------------------------------------------------------------------
    focusedItem: function () {
      return this.itemsById[this.highlightedId] || null;
    },

    focusedCategory: function () {
      const item = this.focusedItem();
      if (!item) return null;
      return (
        this.accessories.find(function (category) {
          return category.handle === item.handle;
        }) || null
      );
    },

    engravingOn: function () {
      if (!this.engraving || this.engraving.available === false) return false;
      return this.engravingDraft.trim().length > 0;
    },

    engravingText: function () {
      return this.engravingDraft.trim().slice(0, (this.engraving && this.engraving.max) || 10);
    },

    /**
     * Where the typed engraving is drawn on the interior shot, as the four numbers the preview's CSS wants.
     *
     * Defaults restated here rather than left to the CSS custom properties' own fallbacks, so the shape is
     * always complete: `engraving` is null on a product with no engraving fee configured, and a template
     * reading `.placement.rotate` off null throws inside an Alpine effect, which fails silently and leaves
     * the whole preview un-updated rather than logging.
     *
     * The values mean: x and y are percentages of the preview box from its centre, rotate is degrees
     * clockwise, size is the type size as a percentage of the box's WIDTH. Same numbers the theme editor's
     * Engraving placement sliders set, measured off design node 445:37253.
     */
    engravingPlacement: function () {
      const place = (this.engraving && this.engraving.placement) || {};
      const num = function (value, fallback) {
        const parsed = parseFloat(value);
        return isFinite(parsed) ? parsed : fallback;
      };

      /*
       * ink and blend are the dark-hat fix, and they are sanitised HERE because this is the one place every
       * caller goes through, and both values end up inside a style attribute.
       *
       * A laser on pale felt scorches DARKER, which is the design's photograph and the default. On black
       * felt it scorches LIGHTER: the fibre lifts and catches the light, so a dark ink multiplied into black
       * felt is invisible. Verified on snowy-river-black, where the default pair rendered as good as nothing.
       * The pair that works there is a pale ink with `screen`.
       *
       * blend is whitelisted rather than passed through: it comes from a metafield a merchant can type into,
       * and it lands in a style attribute, so an arbitrary string is a CSS injection. ink is stripped of the
       * characters that could end one declaration and start another; an otherwise invalid colour just falls
       * back to the CSS default, which is the browser's job rather than ours.
       */
      const blend = place.blend === 'screen' ? 'screen' : 'multiply';
      const ink = String(place.ink || '')
        .replace(/[;{}()<>"']/g, '')
        .trim()
        .slice(0, 32);

      const size = num(place.size, 8);
      const maxWidth = num(place.maxWidth, 46);

      return {
        x: num(place.x, 18),
        y: num(place.y, 22),
        rotate: num(place.rotate, -36),
        size: this.engravingFittedSize(size, maxWidth),
        ink: ink || '#261509',
        blend: blend,
      };
    },

    /**
     * The type size after shrinking a long engraving to fit the brim, both as percentages of the preview's
     * width.
     *
     * A real engraving does NOT do this: the engraver cuts one letter height, so a ten-character name is
     * physically wider than a four-character one. But the preview has a brim to stay on rather than a whole
     * hat band, and at the tuned size ten characters ran past the felt and out over the background, which
     * reads as a bug rather than as a long name. Asked for 24 Aug after seeing exactly that.
     *
     * MEASURED, not counted. Scaling by character count treats "MMMMMMMMMM" and "iiiiiiiiii" as the same
     * string when Times sets them about three times apart, so the narrow one would shrink for no reason
     * while the wide one still overflowed. Canvas gives the real advance width of this text in this face.
     *
     * The maths is unit-free, which is why no DOM measurement is needed: measuring at a nominal 100px gives
     * the width per 100 of font-size, so the size that lands the run exactly on `maxWidth` is
     * maxWidth * 100 / thatWidth, in whatever unit maxWidth was expressed in. Both are percentages of the
     * box, so the result is too.
     *
     * Only ever shrinks. Short text keeps the size the slider set rather than being blown up to fill the
     * width, or "Jo" would arrive in letters twice the height of the design's.
     *
     * Falls back to the unfitted size if canvas is unavailable, so a measurement failure renders a slightly
     * wide engraving rather than none at all.
     */
    engravingFittedSize: function (size, maxWidth) {
      const text = this.engravingText();
      if (!text || !(maxWidth > 0)) return size;

      let width;
      try {
        if (!engravingMetrics) {
          engravingMetrics = document.createElement('canvas').getContext('2d');
        }
        if (!engravingMetrics) return size;
        // Must match .hat-customiser__engraved's font-family in assets/hat-customiser.css.
        engravingMetrics.font = '100px "Times New Roman", Times, serif';
        width = engravingMetrics.measureText(text).width;
      } catch (error) {
        return size;
      }

      if (!isFinite(width) || width <= 0) return size;
      return Math.min(size, (maxWidth * 100) / width);
    },

    /** Committed entries resolved against the catalogue, in stacking then add order. */
    chosen: function () {
      const self = this;

      const resolved = this.entries
        .map(function (entry, addIndex) {
          const item = self.itemsById[String(entry.variantId)];
          if (!item) return null;

          const category = self.accessories.find(function (candidate) {
            return candidate.handle === entry.handle;
          });

          return {
            handle: entry.handle,
            order: category ? category.order : 0,
            variantId: entry.variantId,
            item: item,
            quantity: entry.quantity,
            properties: entry.properties || {},
            addIndex: addIndex,
          };
        })
        .filter(Boolean);

      if (this.engravingOn()) {
        resolved.push({
          handle: 'engraving',
          order: 9999,
          variantId: this.engraving.variantId,
          item: this.engraving,
          quantity: 1,
          properties: { Engraving: this.engravingText() },
          addIndex: resolved.length,
        });
      }

      return resolved.sort(function (a, b) {
        return a.order - b.order || a.addIndex - b.addIndex;
      });
    },

    /** One record per physical unit, so a quantity of 2 renders as two chips and two overlays. */
    units: function () {
      return this.chosen().reduce(function (all, choice) {
        for (let unitIndex = 0; unitIndex < choice.quantity; unitIndex += 1) {
          all.push(
            Object.assign({}, choice, {
              unitIndex: unitIndex,
              key: choice.variantId + ':' + unitIndex,
            })
          );
        }
        return all;
      }, []);
    },

    layers: function () {
      /*
       * Nothing overlays the underside. Pins and feathers sit on the outside of the hat, so while the
       * preview shows the interior -- the engraving view, or a category with no overlay artwork -- there is
       * nothing to draw. Reported against the engraving view, where a feather and two pin markers were
       * rendering over the inside of the crown.
       */
      if (this.showsInterior()) return [];

      const staggerX = Number.isFinite(this.unitStagger) ? this.unitStagger : 4;
      const staggerY = Number.isFinite(this.unitStaggerY) ? this.unitStaggerY : 0;

      /*
       * The spread counts EVERY overlay in a category, not just repeats of one accessory.
       *
       * It used to key off `unitIndex`, which counts copies of the same pin, so a second Spur Pin moved
       * aside but a Spur Pin and a QLD Pin both landed on the category's anchor and read as one smudged
       * badge. Reported 1 September with three different pins stacked on the brim. Every accessory in a
       * category deliberately shares one placement, since that is how the artwork is built, so the
       * category is exactly the right scope to spread within.
       *
       * Per category rather than globally: pins fan out among pins and feathers among feathers, each
       * from its own anchor. A global counter would push the first feather sideways by however many pins
       * happened to be chosen first.
       */
      const slots = {};

      return this.units()
        .filter(function (unit) {
          return Boolean(unit.item.layerImage);
        })
        .map(function (unit) {
          // Where this overlay sits, from the accessory's `layer_placement` metafield with the category
          // as fallback. Absent placement gives an identity transform, so artwork that is already
          // positioned in the file keeps rendering exactly as it did before this existed.
          const place = unit.item.placement || {};
          const num = function (value, fallback) {
            const parsed = parseFloat(value);
            return isNaN(parsed) ? fallback : parsed;
          };

          // Runs in render order, so the slot is stable for a given set of choices.
          slots[unit.order] = slots[unit.order] === undefined ? 0 : slots[unit.order] + 1;
          const slot = slots[unit.order];

          return {
            key: unit.key,
            image: unit.item.layerImage,
            // order * 100 leaves room for a large category without climbing into the next band. Keyed on
            // the slot so a later accessory sits over an earlier one, matching the order they were added.
            zIndex: unit.order * 100 + slot,

            // The per-unit stagger rides ON TOP of the placement, rather than replacing it: placement
            // says where this accessory belongs on the hat, the stagger keeps a second and third copy of
            // the same pin from stacking invisibly on the first.
            //
            // Merchant-set rather than the old hardcoded 3, which visibly overlapped: at a typical pin
            // size 3% of the preview is under twenty pixels, narrower than the pin head itself, so three
            // copies read as one smudged pin. The right number depends on how wide the artwork's ink is,
            // which differs between a pin head and a feather, so it is a slider rather than a guess.
            x: num(place.x, 0) + slot * staggerX,
            y: num(place.y, 0) + slot * staggerY,
            rotate: num(place.rotate, 0),
            scale: num(place.scale, 1),
            origin: place.origin || 'center',
          };
        });
    },

    /**
     * The interior (`-5`) shot while the buyer is in the engraving field, because the engraving is etched
     * on the underside of the brim. Checked before the category branch: engraving is no longer one of the
     * accessory TABS, so `activeHandle` is always Pins or Feathers here, both of which have overlays, and
     * the branch below can never fire for it.
     *
     * The category branch stays for any future overlay-less category. Both fall back to the exterior when
     * the product has no interior image, rather than returning null and rendering nothing.
     *
     * Image swap only. Rendering the typed text onto the hat was a separate, larger option that was never
     * signed off (the client asked to review it against the Figma design and that was left unresolved).
     */
    /**
     * True when the preview is showing the hat's UNDERSIDE.
     *
     * Both `baseImage()` and `layers()` read this, which is the point. They used to decide independently:
     * baseImage swapped to the interior while layers went on drawing pins and feathers, so the engraving
     * view showed accessories floating over the inside of the crown, where none of them physically sit.
     *
     * Requires an interior image to exist. Without one the base falls back to the exterior, and suppressing
     * overlays then would strip the accessories off a view that is still the outside of the hat.
     */
    showsInterior: function () {
      if (this.mode !== 'customise') return false;
      if (!this.bases.interior) return false;
      if (this.engravingFocused) return true;

      const active = this.accessories.find(
        function (category) {
          return category.handle === this.activeHandle;
        }.bind(this)
      );

      const hasOverlay =
        active &&
        (active.items || []).some(function (item) {
          return Boolean(item.layerImage);
        });

      return Boolean(active && !hasOverlay);
    },

    /**
     * True for the whole customise session: the preview shows the close SIDE-ON crop rather than the whole
     * hat front on.
     *
     * The crop is the view the customiser works in. Every overlay is drawn for it, so entering customise
     * mode should arrive there rather than on the exterior shot. Client, 1 September: "it stays on the -1
     * image until you click on a pin or feather and then it switches to -4, can it just load the -4 image
     * once you click the Personalise hat button".
     *
     * It used to also require a highlighted swatch or an attached accessory. That was there to stop the
     * view snapping back to the exterior the moment focus moved, because on the exterior shot the same
     * overlay lands on top of the crown, oversized, with its stem past the brim. Holding the crop for the
     * entire session covers that case and the opening case at once, so the extra condition has nothing
     * left to do.
     *
     * Loses to the engraving, which is checked first in baseImage(): the engraving is on the underside and
     * cannot share a view with anything sitting on the outside.
     *
     * Requires the crop to exist. Without it the base stays on the exterior rather than rendering nothing,
     * which is what a hat photographed without a -4 shot falls back to.
     */
    showsAccessoryView: function () {
      if (this.mode !== 'customise') return false;
      return Boolean(this.bases.accessory);
    },

    /*
     * Priority order, most specific first: the underside while the engraving is in play, then the side-on
     * crop while an accessory is, then the whole hat.
     */
    baseImage: function () {
      if (this.showsInterior()) return this.bases.interior;
      if (this.showsAccessoryView()) return this.bases.accessory;
      return this.bases.exterior;
    },

    addOnCount: function () {
      return this.chosen().reduce(function (sum, choice) {
        return sum + choice.quantity;
      }, 0);
    },

    total: function () {
      return this.chosen().reduce(function (sum, choice) {
        return sum + choice.item.price * choice.quantity;
      }, this.hatPrice);
    },

    formattedTotal: function () {
      return formatMoney(this.total());
    },

    formattedHatPrice: function () {
      return formatMoney(this.hatPrice);
    },

    /** Any cents value, for regions rendering an item price. Items carry cents only, never a string. */
    formattedPrice: function (cents) {
      return formatMoney(cents || 0);
    },

    announce: function (message) {
      // Re-assigning the same string would not re-announce, so the count keeps every message distinct.
      this.announcement = message + ' ' + this.countLabel() + '.';
    },

    previewLabel: function () {
      const chosen = this.chosen();
      const base = this.strings.previewBase || 'Your hat';
      if (!chosen.length) return base;

      const withText = this.strings.previewWith || 'Your hat with';
      return (
        withText +
        ' ' +
        chosen
          .map(function (choice) {
            return choice.quantity > 1
              ? choice.quantity + ' x ' + choice.item.title
              : choice.item.title;
          })
          .join(', ')
      );
    },

    countLabel: function () {
      const count = this.addOnCount();
      const one = this.strings.addOnOne || '1 Add-On';
      // %%count%% deliberately, not a Liquid-looking {{ count }}: verified on staging that Liquid
      // evaluates the braces while rendering the setting into the config payload, so the token arrived
      // as an empty string and the label read " Add-Ons". The addons region uses %%title%% for the same
      // reason.
      const other = this.strings.addOnOther || '%%count%% Add-Ons';
      return count === 1 ? one : other.replace('%%count%%', count);
    },

    withCountLabel: function () {
      const prefix = this.strings.withPrefix || 'With';
      return prefix + ' ' + this.countLabel();
    },

    // ---------------------------------------------------------------------------------------------
    // Add to cart
    // ---------------------------------------------------------------------------------------------
    addToCart: async function () {
      if (this.adding) return;

      if (!this.hatVariantId) {
        this.error = this.strings.chooseOptions || 'Choose your hat options first.';
        return;
      }

      this.adding = true;
      this.error = '';

      // Tagged even when nothing is chosen. The cart transform ignores a group of one, so the extra
      // property is inert and it keeps this free of a branch that would only save a few bytes.
      /*
       * `editingUnits` hats, each its own bundle group. It is 1 for a fresh build, so the ordinary PDP path
       * is unchanged; on an EDIT it is the count carried in the url, which is what makes the replacement
       * the same size as the line it replaces instead of collapsing a 3-hat line to 1.
       *
       * Built by the shared core so this and the cart's own controls cannot drift on the one detail that
       * matters here: a DISTINCT _bundleId per hat, since the transform merges by that id and identical
       * merged lines are what Shopify collapses into a line reporting the true count.
       */
      const items = window.CartBundleGroups.buildItems(
        this.hatVariantId,
        this.chosen().map(function (choice) {
          return { id: choice.variantId, quantity: choice.quantity, properties: choice.properties };
        }),
        this.editingUnits
      );

      try {
        /*
         * #388 C7: retire the line being replaced BEFORE adding the replacement.
         *
         * This was the other way round, on the reasoning that a failed add should never lose the buyer's
         * bundle. That reasoning was wrong about the mechanics: a merged line's key is
         * `<parentVariantId>:<hash>`, and adding another bundle re-hashes it, so the key captured when the
         * page rendered is STALE by the time the add completes. change.js then no-ops against a key that
         * no longer exists, and because the failure was deliberately swallowed the result was silent --
         * EDIT appeared to work and left the original line sitting in the cart as a duplicate.
         *
         * Removing first uses the key while it is still valid. The risk it was avoiding is smaller than it
         * looked: the customiser still holds the whole selection on screen, so a failed add leaves the
         * buyer able to press the button again, and the error below now tells them to.
         */
        if (this.editingLineKey) {
          const retiring = this.editingLineKey;
          this.editingLineKey = null;
          await removeLine(retiring);
        }

        await addItems(items);
      } catch (requestError) {
        this.error = requestError.message || this.strings.addFailed || 'This could not be added to your bag.';
      } finally {
        this.adding = false;
      }
    },
  });
});

/**
 * #388 C7: removes the cart line an EDIT session is replacing.
 *
 * `cart/change.js` with quantity 0 rather than a `url_to_remove` navigation, because the buyer stays on
 * the PDP with the drawer open and a page load would throw that away. Setting 0 on a merged bundle line
 * removes the whole group, hat and accessories together, which is verified behaviour and the same thing
 * the cart's own REMOVE link does.
 *
 * Emits `cart:change` so the drawer redraws without the retired line. Nothing is thrown to the caller
 * beyond a failed request, because the add it follows has already succeeded.
 */
async function removeLine(lineKey) {
  /*
   * EVERY group behind the line, not one.
   *
   * A single `change.js quantity: 0` reaches one bundle group, because the visible line is N collapsed
   * groups and the key addresses one of them. That is what split a 3-hat line into 1 + 2 on EDIT: the
   * removal took one hat and the add put one back. Delegated to the shared core so this and the cart's own
   * Qty and Size controls share one implementation of the loop and the signature it matches on.
   */
  await window.CartBundleGroups.removeAll(lineKey);

  const cart = await window.CartBundleGroups.getCart();
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'variant:change', cart: cart },
    })
  );
}

/**
 * Posts every line in one request, then hands the theme the payload its cart drawer expects.
 * `CartDrawer` reads `cart.sections[sectionId]` without guarding, so the sections have to be
 * collected before the request and carried through to the event.
 */
async function addItems(items) {
  const sections = [];
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
  );

  const response = await fetch(Shopify.routes.root + 'cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest', // Needed for Shopify to check inventory
    },
    body: JSON.stringify({ items: items, sections: sections }),
  });

  const payload = await response.json();

  if (!response.ok) {
    throw new Error(payload.description || payload.message || 'This could not be added to your bag.');
  }

  const cart = await (await fetch(Shopify.routes.root + 'cart.js')).json();
  cart.sections = payload.sections;

  document.documentElement.dispatchEvent(
    new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart: cart },
    })
  );
}

/**
 * The theme has no money formatter, so this is the smallest one that covers `shop.money_format`.
 * ponytail: handles the four decimal/comma permutations Shopify ships. A shop using apostrophe or
 * space separators would need the remaining tokens adding.
 */
function formatMoney(cents) {
  const settings = (window.themeVariables && window.themeVariables.settings) || {};

  // Honour the merchant's currency-code setting, or the running total reads $350.00 beside a theme
  // price block reading $350.00 AUD on the same row.
  const format =
    (settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat) ||
    settings.moneyFormat ||
    '${{amount}}';

  return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_match, token) {
    const decimals = token.indexOf('no_decimals') === -1 ? 2 : 0;
    const thousands = token.indexOf('comma_separator') === -1 ? ',' : '.';
    const decimalPoint = thousands === ',' ? '.' : ',';

    const amount = (cents / 100).toFixed(decimals);
    const parts = amount.split('.');
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

    return parts[1] ? whole + decimalPoint + parts[1] : whole;
  });
}
